import type { ReactNode } from 'react';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Select from '@material-ui/core/Select';

/** Row of filters above a view; wraps on small screens. */
export function FilterBar({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 16,
        marginBottom: 24,
      }}
    >
      {children}
    </div>
  );
}

/** « Derniers N jours » selector shared by every time-windowed page. */
export function PeriodSelect({
  id,
  value,
  options,
  onChange,
  label = 'Période',
  format = days => `Derniers ${days} jours`,
}: {
  id: string;
  value: number;
  options: number[];
  onChange: (days: number) => void;
  label?: string;
  /** Wording of an option, « Derniers N jours » by default. */
  format?: (value: number) => string;
}) {
  return (
    <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <Select
        labelId={`${id}-label`}
        label={label}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
      >
        {options.map(days => (
          <MenuItem key={days} value={days}>
            {format(days)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

/** Pick one value of a list, or all of them (the empty value). */
export function OptionSelect({
  id,
  label,
  value,
  options,
  onChange,
  allLabel = 'Tous',
  minWidth = 200,
}: {
  id: string;
  label: string;
  /** `''` means no filter. */
  value: string;
  options: string[];
  onChange: (value: string) => void;
  allLabel?: string;
  minWidth?: number;
}) {
  // A value from the URL that is not (or no longer) an option still shows as selected.
  const choices =
    value && !options.includes(value) ? [value, ...options] : options;
  return (
    <FormControl variant="outlined" size="small" style={{ minWidth }}>
      {/* Always shrunk: the empty value shows the « all » label, not a placeholder. */}
      <InputLabel id={`${id}-label`} shrink>
        {label}
      </InputLabel>
      <Select
        labelId={`${id}-label`}
        label={label}
        value={value}
        displayEmpty
        input={<OutlinedInput notched label={label} />}
        onChange={e => onChange(String(e.target.value))}
      >
        <MenuItem value="">{allLabel}</MenuItem>
        {choices.map(option => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

/** Validates a raw URL value against the allowed options. */
export const parseOption =
  (options: number[], fallback: number) =>
  (raw: string | null): number => {
    const parsed = Number(raw);
    return options.includes(parsed) ? parsed : fallback;
  };
